import React, { useState } from 'react';
import { Stack, Typography, CircularProgress, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BlogsTabListItem } from './BlogsTabListItem';
import { useGetRelatedBlogs } from '../api/getRelatedBlogs';
import { useGetRelatedBlogsBySlug } from '../api/getRelatedBlogsBySlug';
import { PaperCard } from 'components/Card';
import { Pagination } from 'components/Pagination';

export const BlogsTab = ({ companyId, currentTab, slug }) => {
  const { t } = useTranslation();
  const blogsTabName = "blog";
  const [page, setPage] = useState(1);

  const { data: { blogs: blogsById, total: totalById } = {}, isLoading: loadingById, isError: isErrorById, error: errorById } = useGetRelatedBlogs(companyId, {
    page: page - 1,
    limit: 6,
    queryOptions: {
      enabled: currentTab === blogsTabName && !!companyId,
    },
  });

  const { data: { blogs: blogsBySlug, total: totalBySlug } = {}, isLoading: loadingBySlug, isError: isErrorBySlug, error: errorBySlug } = useGetRelatedBlogsBySlug(slug, {
    page: page - 1,
    limit: 6,
    queryOptions: {
      enabled: currentTab === blogsTabName && !!slug,
    },
  });

  const isLoading = loadingById || loadingBySlug;
  const isError = isErrorById || isErrorBySlug;
  const error = errorById || errorBySlug;
  const blogs = slug ? blogsBySlug : blogsById;
  const total = slug ? totalBySlug : totalById;

  if (currentTab !== blogsTabName) return null;

  const handlePageChange = (_, page) => setPage(page);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (isError) {
      return <Typography color="error">{error?.message || t('website:publicProfile.blogs.loadError')}</Typography>;
    }

    if (total > 0 && blogs && blogs.length > 0) {
      return (
        <Stack direction="column" gap={2}>
          {blogs
            .reduce((rows, item, index) => {
              if (index % 3 === 0) {
                rows.push([]);
              }
              rows[rows.length - 1].push(<BlogsTabListItem key={item.blog_id} blog={item} isPublicProfile={!!slug} />);
              return rows;
            }, [])
            .map((row, rowIndex, allRows) => {
              const itemsInRow = row.length;
              const emptySlotsNeeded = 3 - itemsInRow;

              return (
                <Stack key={rowIndex} direction="row" gap={2}>
                  {row.map((item) => item)}
                  {rowIndex === allRows.length - 1 && emptySlotsNeeded > 0 && (
                    Array.from({ length: emptySlotsNeeded }).map((_, index) => (
                      <PaperCard
                        sx={{
                          flex: 1,
                          boxShadow: 0,
                          borderRadius: 1,
                          p: 2,
                          visibility: "hidden"
                        }}
                        key={`empty-${rowIndex}-${index}`}
                        flex={1}
                      >
                        <></>
                      </PaperCard>
                    ))
                  )}
                </Stack>
              );
            })}
        </Stack>
      );
    }

    return <Typography>{t('website:publicProfile.blogs.empty')}</Typography>;
  };

  return (
    <Stack spacing={2} sx={{ p: { xs: 0, sm: 0 } }}>
      {renderContent()}
      <Stack direction="row" justifyContent="center">
        <Pagination
          page={page}
          handlePageChange={handlePageChange}
          itemCount={total}
          itemsPerPage={6}
          prevLabel={t('website:common.pagination.prev')}
          nextLabel={t('website:common.pagination.next')}
        />
      </Stack>
    </Stack>
  );
};

